import { type DayOfWeek, frenchDaysOfWeek } from '@gozoki/api-types'
import { DateTime } from 'luxon'

const LUXON_WEEKDAY: Record<DayOfWeek, number> = {
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
  sunday: 7,
}

export interface SimulationDefaults {
  dayOfWeek: DayOfWeek
  hour: number
  minute: number
}

const FALLBACK_DAYS: ReadonlySet<DayOfWeek> = new Set(['friday', 'saturday', 'sunday'])

/**
 * Returns the default day-of-week + hour to pre-fill the simulation modal:
 * - today's day-of-week and current hour, except
 * - if today is Friday/Saturday/Sunday → Thursday at the same hour
 *   (no production on Saturday means a vendredi-based simulation always returns 0)
 */
export const resolveDefaultSimulation = (
  now: DateTime = DateTime.now().setZone('Europe/Paris'),
): SimulationDefaults => {
  const todayDow = now.toFormat('cccc').toLowerCase() as DayOfWeek
  const fallback = FALLBACK_DAYS.has(todayDow)
  return {
    dayOfWeek: fallback ? 'thursday' : todayDow,
    hour: now.hour,
    minute: now.minute,
  }
}

/**
 * Resolves a (dayOfWeek, hour, minute) selection to a concrete `DateTime`,
 * picking the **most recent** occurrence of that weekday relative to `now`
 * (today if same weekday, else N days back where N ∈ [1, 6]).
 */
export const buildReferenceDateTime = (
  defaults: SimulationDefaults,
  now: DateTime = DateTime.now().setZone('Europe/Paris'),
): DateTime => {
  const target = LUXON_WEEKDAY[defaults.dayOfWeek]
  const delta = (now.weekday - target + 7) % 7
  return now
    .startOf('day')
    .minus({ days: delta })
    .set({ hour: defaults.hour, minute: defaults.minute, second: 0, millisecond: 0 })
}

export const DAY_OPTIONS: { value: DayOfWeek; label: string }[] = (
  ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as DayOfWeek[]
).map((d) => ({ value: d, label: frenchDaysOfWeek[d] }))
